import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import React, { useState, useEffect } from "react";
import CreatorForm from "./Components/CreatorForm";
import HomePage from "./pages/HomePage";
import IconSignUp from "./Components/IconSignUp";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PiiPolicy from "./pages/PiiPolicy";
import CcPolicy from "./pages/CcPolicy";
import CompliancePolicy from "./pages/CompliancePolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import SupportPage from "./pages/SupportPage";
import AgeVerificationModal from "./Components/AgeVerificationModal";
import { isAgeRestricted, getExitUrl } from "./config/ageRestrictedCreators";

const homepageRedirectUsernames = [
  "itsstellabrooks",
  "emmalaneofficial",
  "rosechevelle",
  "itskiara_moon",
  "brandiandrews_official",
  "kellybobsx",
];

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/pii-policy" element={<PiiPolicy />} />
        <Route path="/cc-policy" element={<CcPolicy />} />
        <Route path="/compliance-policy" element={<CompliancePolicy />} />
        <Route
          path="/terms-and-conditions"
          element={<TermsAndConditions />}
        />
        <Route path="/support" element={<SupportPage />} />
        {homepageRedirectUsernames.map((username) => (
          <Route
            key={username}
            path={`/${username}`}
            element={<Navigate to="/" replace />}
          />
        ))}
        <Route path="/:username" element={<UserPage />} />
        <Route path="/creators" element={<CreatorForm />} />
        <Route path="/icon/signup" element={<IconSignUp />} />
      </Routes>
    </Router>
  );
}

function UserPage() {
  const { username } = useParams();
  const [profile, setProfile] = useState({
    name: null,
    photo: null,
    bio: null,
  });
  const [loading, setLoading] = useState(true);
  const [showAgeModal, setShowAgeModal] = useState(false);

  useEffect(() => {
    // Check if age verification is required and if user has already verified
    if (isAgeRestricted(username)) {
      const storageKey = `age_verified_${username}`;
      const hasVerified = sessionStorage.getItem(storageKey) === 'true';
      
      if (!hasVerified) {
        setShowAgeModal(true);
        // Continue loading profile in background
      }
    }

    const targetUrl = `https://telegram.me/${username}`;
    let cancelled = false;
    let activeController = null;
    let cancelRetryWait = null;

    setLoading(true);
    setProfile({ name: null, photo: null, bio: null });

    const CORS_PROXIES = [
      `https://proxy-us.corsfix.com/?url=${encodeURIComponent(targetUrl)}`,
      `https://proxy-ap.corsfix.com/?url=${encodeURIComponent(targetUrl)}`,
      `https://proxy.corsfix.com/?url=${encodeURIComponent(targetUrl)}`,
      `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(targetUrl)}`,
      `https://api.cors.lol/?url=${encodeURIComponent(targetUrl)}`,
      `https://cors-anywhere.com/${targetUrl}`,
      `https://api.allorigins.win/raw?url=${encodeURIComponent(targetUrl)}`,
    ];

    const fetchWithProxy = async (proxyUrl) => {
      const controller = new AbortController();
      activeController = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);

      try {
        const response = await fetch(proxyUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`Proxy returned HTTP ${response.status}`);
        const text = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");

        if (doc.querySelector('.tgme_page_empty')) {
          throw new Error('User not found');
        }

        const photoElement = doc.querySelector(".tgme_page_photo_image");
        const nameElement = doc.querySelector(".tgme_page_title span");
        const bioElement = doc.querySelector(".tgme_page_description");
        const name = nameElement?.textContent?.trim();

        if (!name) throw new Error('Invalid Telegram profile response');

        return {
          photo: photoElement?.getAttribute('src') || null,
          name,
          bio: bioElement?.textContent?.trim() || null
        };
      } finally {
        clearTimeout(timeout);
        activeController = null;
      }
    };

    const waitBeforeRetry = (delay) => new Promise((resolve) => {
      const timeout = setTimeout(() => {
        cancelRetryWait = null;
        resolve();
      }, delay);

      cancelRetryWait = () => {
        clearTimeout(timeout);
        cancelRetryWait = null;
        resolve();
      };
    });

    const fetchProfile = async () => {
      while (!cancelled) {
        for (const proxy of CORS_PROXIES) {
          try {
            const data = await fetchWithProxy(proxy);
            if (cancelled) return;
            setProfile(data);
            setLoading(false);
            return;
          } catch (error) {
            if (cancelled) return;
            console.error(`Error with proxy ${proxy}:`, error);
          }
        }

        if (cancelled) return;
        await waitBeforeRetry(1000);
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
      activeController?.abort();
      cancelRetryWait?.();
    };
  }, [username]);

  const handleSendMessage = () => {
    window.location.href = `https://telegram.me/${username}`;
  };

  const handleAgeVerificationEnter = () => {
    const storageKey = `age_verified_${username}`;
    sessionStorage.setItem(storageKey, 'true');
    
    // Redirect to Telegram directly (same as Send Message)
    window.location.href = `https://telegram.me/${username}`;
  };

  const handleAgeVerificationExit = () => {
    // Redirect to custom exit URL for this creator
    const exitUrl = getExitUrl(username);
    window.location.href = exitUrl;
  };

  const LoadingState = () => (
    <>
      <div className="profile-photo-placeholder shimmer"></div>
      <div className="name-placeholder shimmer"></div>
      <h2 className="username">@{username}</h2>
      {/* Bio placeholder intentionally hidden */}
      {/* <div className="bio-placeholder shimmer"></div> */}
    </>
  );

  return (
    <div className="main-container">
      <style>{styles}</style>
      
      {/* Age Verification Modal */}
      {showAgeModal && (
        <AgeVerificationModal 
          onEnter={handleAgeVerificationEnter}
          onExit={handleAgeVerificationExit}
        />
      )}
      
      <header className="header">
        <div className="header-container">
          <div className="header-icon"></div>
          <h1 className="header-title">Telegram</h1>
        </div>
      </header>
      <div className="card">
        {loading && <LoadingState />}
        {!loading && (
          <>
            {profile.photo && (
              <img
                src={profile.photo}
                alt={profile.name || username}
                className="profile-photo"
              />
            )}
            <h1 className="name">{profile.name || `@${username}`}</h1>
            <h2 className="username">@{username}</h2>
            {/* Bio intentionally hidden on creator page */}
            {/* {profile.bio && <p className="bio">{profile.bio}</p>} */}
          </>
        )}
        <button onClick={handleSendMessage} className="button" disabled={loading}>
          SEND MESSAGE
        </button>
      </div>

      <div className="bottom-cta" aria-label="New creator sign up">
        <div className="bottom-cta-inner">
          <div className="bottom-cta-text">New Creator?</div>
          <a
            href="https://creators.tlink.me/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="bottom-cta-link"
          >
            Sign Up
          </a>
        </div>
      </div>
    </div>
  );
}

const styles = `
  :root {
    --card-bg: white;
    --text-color: #000000;
    --text-secondary: #787A7C;
    --text-tertiary: #000000;
    --button-gradient: linear-gradient(180deg, #006FC9 0%, #006FC9 100%);
    --button-hover-gradient: linear-gradient(180deg, #006FC9 0%, #00569c 100%);
    --background-text-opacity: 0.2;
    --bg-gradient: linear-gradient(180deg, #C5CF9E 0%, #579E73 100%);
    --header-bg: #ffffff;
    --shimmer-bg: #f6f7f8;
    --shimmer-highlight: rgba(255, 255, 255, 0.8);
  }

  @media (prefers-color-scheme: dark) {
    :root {
      --card-bg: #1E1E1E;
      --text-color: #ffffff;
      --text-secondary: #787A7C;
      --text-tertiary: #ffffff;
      --button-gradient: linear-gradient(180deg, #1C93E3 0%, #1C93E3 100%);
      --button-hover-gradient: linear-gradient(180deg, #1C93E3 0%, #00569c 100%);
      --background-text-opacity: 0.2;
      --bg-gradient: linear-gradient(180deg, #000000 0%, #000000 100%);
      --header-bg: #1E1E1E;
      --shimmer-bg: #2a2a2a;
      --shimmer-highlight: rgba(255, 255, 255, 0.1);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .shimmer {
    animation: shimmer 2s infinite linear;
    background: linear-gradient(to right, var(--shimmer-bg) 4%, var(--shimmer-highlight) 25%, var(--shimmer-bg) 36%);
    background-size: 1000px 100%;
  }

  .profile-photo-placeholder {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    margin: 0 auto 16px auto;
  }

  .name-placeholder {
    width: 200px;
    height: 32px;
    border-radius: 4px;
    margin: 0 auto 4px auto;
  }

  .bio-placeholder {
    width: 280px;
    height: 30px;
    border-radius: 4px;
    margin: 0 auto 20px auto;
  }

  body {
    margin: 0;
    font-family: 'Lexend', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  .main-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: var(--bg-gradient);
    min-height: 100vh;
    padding-bottom: 86px;
    box-sizing: border-box;
  }

  .bottom-cta {
    position: fixed;
    
    left: 0;
    right: 0;
    bottom: 10px;
    z-index: 0;
    display: flex;
    justify-content: center;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    pointer-events: none;
  }

  .bottom-cta-inner {
    pointer-events: auto;
    background: transparent;
    border: 0;
    border-radius: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    color: rgba(255, 255, 255, 0.92);
    font-family: 'Lexend', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  .bottom-cta-text {
    font-size: 12px;
    opacity: 50%;
    font-weight: 500;
    letter-spacing: -0.2px;
    text-decoration: none;
  }

  .bottom-cta-link {
    font-size: 12px;
    font-weight: 500;
    opacity: 80%;
    color: rgba(255, 255, 255, 0.98);
    text-decoration: underline;
  
  }

  .bottom-cta-link:focus {
    outline: 2px solid rgba(255, 255, 255, 0.6);
    outline-offset: 3px;
    border-radius: 999px;
  }

  .header {
    width: 100%;
    background: var(--header-bg);
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
    position: fixed;
    top: 0;
    padding: 1rem;
    z-index: 1000;
  }

  .header-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 10px 20px 10px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .header-icon {
    width: 30px;
    height: 30px;
    background-repeat: no-repeat;
    background-size: contain;
    background-image: url('/telegraml.svg');
  }

  @media (prefers-color-scheme: dark) {
    .header-icon {
      background-image: url('/telegramd.svg');
    }
  }

  .header-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-tertiary);
    margin: 0;
  }

  .card {
    margin-left:16px;
    margin-right:16px;
    position: relative;
    background: var(--card-bg);
    margin-top: 80px;
    padding: 32px 0px 22px 0px;
    border-radius: 15px;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.06);
    text-align: center;
    min-width: 320px;
    width: 90%;
    max-width: 400px;
  }

  .name {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.9px;
    margin: 0 0 8px 0;
    color: var(--text-color);
  }

  .username {
    font-size: 15px;
    font-weight: 500;
    color: var(--text-secondary);
    margin: 0 0 16px 0;
  }

  .bio {
    font-size: 16px;
    color: var(--text-tertiary);
    margin: 10px 0;
    padding: 0px 0px 20px 0px;
    white-space: pre-wrap;
    word-break: break-word;
    padding: 0.5rem 1rem 1rem 1rem;
  }

  .button {
    background-image: var(--button-gradient);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 100px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
    font-size: 14px;
    text-transform: uppercase;
  }

  .loading {
    color: var(--text-secondary);
    font-size: 14px;
    margin: 20px;
  }

  .error {
    color: red;
    font-size: 14px;
    margin: 20px;
  }

  .profile-photo {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 10px;
  }

  .content {
    margin-top: 100px;
    text-align: center;
    color: var(--text-color);
  }

  .content h2 {
    font-size: 24px;
    margin-bottom: 16px;
  }

  .content p {
    font-size: 16px;
    color: var(--text-secondary);
  }

  @media screen and (max-width: 600px) {
    .header-container {
      padding: 10px 16px;
    }

    .card {
      margin-left: 12px;
      margin-right: 12px;
    }
  }
`;

export default App;
